/**
 * FriendSelectModal.js - 친구 선택 모달 (다중 선택 체크박스)
 *
 * 사용처:
 * - 방 생성 후 친구 초대 (RoomCreationModal 2단계)
 * - 기존 방에 친구 초대 (RoomManagementModal → RoomInfoTab)
 *
 * Props:
 * - isOpen: boolean
 * - onClose: () => void  (건너뛰기 / 닫기)
 * - onConfirm: (selectedIds: string[], finalRoomName?: string) => void
 * - title: string (선택적)
 * - inviteCode: string (선택적) - 있으면 링크 복사/공유 섹션 표시
 * - roomName: string (선택적) - 방 이름 (초기값)
 * - ownerName: string (선택적) - 방장 이름 (방 이름 미리보기 시작값)
 */
import React, { useState, useEffect, useMemo } from 'react';
import { auth } from '../../config/firebaseConfig';
import { X, UserCheck, Copy, Share2, Check, Link, UserPlus } from 'lucide-react';
import { API_BASE_URL } from '../../config/apiConfig';

const API_BASE = API_BASE_URL;

// 선택된 친구 이름으로 방 이름 생성 (60자 초과 시 마지막 이름 잘라냄)
const buildRoomName = (ownerName, selectedFriends) => {
  if (!ownerName) return '';
  if (selectedFriends.length === 0) return ownerName;
  let result = ownerName;
  for (const f of selectedFriends) {
    const friendName = f.firstName || '';
    const candidate = result + ', ' + friendName;
    if (candidate.length > 60) {
      const remaining = 60 - result.length - 2; // 2 = ', '.length
      if (remaining > 3) {
        result = result + ', ' + friendName.slice(0, remaining - 3) + '...';
      }
      break;
    }
    result = candidate;
  }
  return result;
};

const FriendSelectModal = ({ isOpen, onClose, onConfirm, title = '친구 선택', inviteCode, roomName, ownerName, roomId }) => {
  const [friends, setFriends] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [roomMemberIds, setRoomMemberIds] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    setSelected([]);
    const load = async () => {
      setLoading(true);
      try {
        const token = await auth.currentUser?.getIdToken();
        const [friendsRes, roomRes] = await Promise.all([
          fetch(`${API_BASE}/api/friends`, { headers: { Authorization: `Bearer ${token}` } }),
          roomId ? fetch(`${API_BASE}/api/coordination/rooms/${roomId}`, { headers: { Authorization: `Bearer ${token}` } }) : Promise.resolve(null),
        ]);
        const friendsData = await friendsRes.json();
        setFriends(friendsData.friends || []);
        if (roomRes && roomRes.ok) {
          const roomData = await roomRes.json();
          const memberIds = (roomData.members || []).map(m => (m.user?._id || m.user?.id || m.user)?.toString()).filter(Boolean);
          const ownerId = (roomData.owner?._id || roomData.owner?.id || roomData.owner)?.toString();
          if (ownerId) memberIds.push(ownerId);
          setRoomMemberIds(memberIds);
        }
      } catch {
        setFriends([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isOpen, roomId]);

  if (!isOpen) return null;

  const toggle = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const selectedFriends = friends.filter(f => selected.includes(f._id || f.id));
  const previewRoomName = ownerName ? buildRoomName(ownerName, selectedFriends) : null;

  const inviteUrl = inviteCode ? `${window.location.origin}/invite/${inviteCode}` : null;

  const handleCopyLink = () => {
    if (!inviteUrl) return;
    navigator.clipboard.writeText(inviteUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }).catch(() => {
      const el = document.createElement('textarea');
      el.value = inviteUrl;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const handleShareLink = async () => {
    if (!inviteUrl) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: roomName ? `"${roomName}" 초대` : '방 초대',
          text: roomName ? `"${roomName}"에 초대합니다!` : '방에 초대합니다!',
          url: inviteUrl,
        });
      } catch (e) {
        if (e.name !== 'AbortError') handleCopyLink();
      }
    } else {
      handleCopyLink();
    }
  };

  const handleConfirm = () => {
    onConfirm(selected, previewRoomName || roomName);
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: '#fff', borderRadius: '16px', width: '85%', maxWidth: '360px',
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
      }}>
        {/* 헤더 */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '14px 16px', borderBottom: '1px solid #e5e7eb',
        }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#111827' }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex' }}>
            <X size={20} color="#6b7280" />
          </button>
        </div>

        {/* 삼색 초대 버튼 */}
        {inviteCode && (
          <div style={{ padding: '12px 16px 4px', borderBottom: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button
              onClick={handleCopyLink}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 14, padding: '12px 14px', background: '#f8fafc', borderRadius: 12, border: 'none', cursor: 'pointer' }}
            >
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {copied ? <Check size={18} color="#16a34a" /> : <Link size={18} color="#2563eb" />}
              </div>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: copied ? '#16a34a' : '#111827' }}>{copied ? '복사됨!' : '링크 복사'}</div>
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 1 }}>초대 링크를 클립보드에 복사</div>
              </div>
            </button>
            <button
              onClick={handleShareLink}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 14, padding: '12px 14px', background: '#f8fafc', borderRadius: 12, border: 'none', cursor: 'pointer' }}
            >
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Share2 size={18} color="#16a34a" />
              </div>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>링크 공유</div>
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 1 }}>카카오톡, 문자 등으로 공유</div>
              </div>
            </button>
            <button
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 14, padding: '12px 14px', background: '#f8fafc', borderRadius: 12, border: 'none', cursor: 'default' }}
            >
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <UserPlus size={18} color="#7c3aed" />
              </div>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>친구 초대</div>
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 1 }}>아래 목록에서 선택해서 초대</div>
              </div>
            </button>
          </div>
        )}

        {/* 목록 (4명 고정 높이 + 스크롤) */}
        <div style={{ height: '232px', overflowY: 'auto', padding: '8px' }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '36px' }}>
              <div style={{
                width: '28px', height: '28px', border: '3px solid #e2e8f0',
                borderTop: '3px solid #3b82f6', borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }} />
            </div>
          ) : friends.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '36px', color: '#94a3b8' }}>
              <UserCheck size={36} style={{ margin: '0 auto 8px', display: 'block' }} />
              <p style={{ margin: 0, fontSize: '14px' }}>친구가 없습니다.</p>
              <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#cbd5e1' }}>먼저 친구를 추가해보세요!</p>
            </div>
          ) : (
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {friends.map((f) => {
                const id = f._id || f.id;
                const isAlreadyIn = roomMemberIds.includes(id?.toString());
                const isSelected = selected.includes(id);
                const initial = (f.firstName?.[0] || f.email?.[0] || '?').toUpperCase();
                return (
                  <li
                    key={id}
                    onClick={() => { if (!isAlreadyIn) toggle(id); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '12px',
                      padding: '10px 12px', borderRadius: '8px',
                      cursor: isAlreadyIn ? 'default' : 'pointer',
                      background: isAlreadyIn ? '#f0fdf4' : isSelected ? '#eff6ff' : 'transparent',
                      transition: 'background 0.15s',
                    }}
                  >
                    <div style={{
                      width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0,
                      background: isAlreadyIn ? '#bbf7d0' : isSelected ? '#3b82f6' : '#dbeafe',
                      color: isAlreadyIn ? '#16a34a' : isSelected ? '#fff' : '#1d4ed8',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 700, fontSize: '14px', overflow: 'hidden',
                    }}>
                      {f.profileImage
                        ? <img src={f.profileImage} alt={initial} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : initial}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: '14px', color: isAlreadyIn ? '#15803d' : '#111827' }}>
                        {f.firstName}{f.lastName}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>
                        {isAlreadyIn ? '이미 방에 있음' : f.email}
                      </div>
                    </div>
                    <div style={{
                      width: '20px', height: '20px', borderRadius: '50%', flexShrink: 0,
                      border: `2px solid ${isAlreadyIn ? '#16a34a' : isSelected ? '#3b82f6' : '#d1d5db'}`,
                      background: isAlreadyIn ? '#16a34a' : isSelected ? '#3b82f6' : '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {(isAlreadyIn || isSelected) && (
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#fff' }} />
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* 하단 버튼 */}
        <div style={{ display: 'flex', gap: '8px', padding: '12px 16px', borderTop: '1px solid #e5e7eb' }}>
          <button
            onClick={onClose}
            style={{
              flex: 1, padding: '10px', background: '#f3f4f6', border: 'none',
              borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', color: '#374151',
            }}
          >
            건너뛰기
          </button>
          <button
            onClick={handleConfirm}
            disabled={selected.length === 0}
            style={{
              flex: 2, padding: '10px',
              background: selected.length > 0 ? '#3b82f6' : '#93c5fd',
              border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600,
              cursor: selected.length > 0 ? 'pointer' : 'not-allowed', color: '#fff',
            }}
          >
            초대{selected.length > 0 ? ` (${selected.length}명)` : ''}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FriendSelectModal;
